import React from "react";
import { Pressable, ActivityIndicator } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "../Icon";

interface StaleDataBannerProps {
  /** Whether the device is offline */
  isOffline?: boolean;
  /** Whether a retry is in progress */
  isRetrying?: boolean;
  /** Retry callback - makes the banner tappable */
  onRetry?: () => void;
  /** Custom message (overrides default) */
  message?: string;
}

/**
 * Subtle banner shown when displaying cached data after a fetch error.
 * Non-blocking - allows users to continue viewing cached content.
 */
export const StaleDataBanner: React.FC<StaleDataBannerProps> = ({
  isOffline = false,
  isRetrying = false,
  onRetry,
  message,
}) => {
  const { theme } = useUnistyles();

  const getMessage = () => {
    if (message) return message;
    if (isRetrying) return "Refreshing...";
    if (isOffline) return "You're offline. Showing cached data.";
    return "Unable to load latest data. Showing last known state.";
  };

  // Use muted colors for offline (less alarming), warning colors for stale data
  const bannerBg = isOffline ? theme.colors.grey[200] : theme.colors.warning[100];
  const textColor = isOffline ? theme.colors.grey[700] : theme.colors.warning[800];
  const iconColor = isOffline ? theme.colors.grey[500] : theme.colors.warning[600];

  const content = (
    <View flexDirection="row" alignItems="center" px="4xl" py="sm" gap="sm" style={{ backgroundColor: bannerBg }}>
      {isRetrying ?
        <ActivityIndicator size="small" color={iconColor} />
      : <Icon name={"warningOutline"} size={18} color={iconColor} />}
      <Text textStyle="sm/medium" style={{ color: textColor, flex: 1 }}>
        {getMessage()}
      </Text>
      {onRetry && !isRetrying && !isOffline && <Icon name="refresh" size={18} color={iconColor} />}
    </View>
  );

  // Make tappable if retry is available
  if (onRetry && !isRetrying && !isOffline) {
    return <Pressable onPress={onRetry}>{content}</Pressable>;
  }

  return content;
};
